import React, { useEffect, useState } from 'react';
import { getItemStatus } from '../utils/dateUtils';
import { fetchAllMedia } from '../utils/mediaUtils';
import MediaCard from '../components/MediaCard';
import LoadingSpinner from '../components/LoadingSpinner';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import SortByAlphaIcon from '@mui/icons-material/SortByAlpha';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

function Catalogue({ onItemClick }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('movie');
  const [sortBy, setSortBy] = useState('alpha');

  useEffect(() => {
    const fetchItems = async () => {
      try {
        const all = await fetchAllMedia();
        setItems(all);
      } catch (err) {
        setError('Failed to load catalogue.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchItems();
  }, []);

  const filtered = items.filter(item => {
    if (getItemStatus(item) !== 'catalogue') return false;
    const matchesSearch = item.title.toLowerCase().includes(search.toLowerCase());
    return matchesSearch && item.type === tab;
  });

  const getAddedTime = (item) => {
    const value = item.addedDate ?? item.addedAt ?? item.createdAt ?? item.created_at;
    if (!value) return 0;
    if (typeof value?.toMillis === 'function') return value.toMillis();
    if (typeof value?.toDate === 'function') return value.toDate().getTime();
    const parsed = new Date(value).getTime();
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const sortedFiltered = [...filtered].sort((a, b) => {
    if (sortBy === 'addedDate') {
      return getAddedTime(b) - getAddedTime(a);
    }
    return (a.title || '').localeCompare(b.title || '');
  });

  const movieCount = items.filter(item => {
    if (getItemStatus(item) !== 'catalogue') return false;
    const matchesSearch = item.title.toLowerCase().includes(search.toLowerCase());
    return matchesSearch && item.type === 'movie';
  }).length;

  const tvCount = items.filter(item => {
    if (getItemStatus(item) !== 'catalogue') return false;
    const matchesSearch = item.title.toLowerCase().includes(search.toLowerCase());
    return matchesSearch && item.type === 'tv';
  }).length;

  const getSortLabel = (value) => (value === 'addedDate' ? 'Recently Added' : 'Title (A–Z)');

  if (loading) return <LoadingSpinner message="Loading catalogue…" />;

  return (
    <Container maxWidth={false} disableGutters sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, px: { xs: 2, sm: 3 } }}>
        <TextField
          size="small"
          placeholder="Search…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          sx={{ flex: 1 }}
        />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="catalogue-sort-by-label">Sort by</InputLabel>
          <Select
            labelId="catalogue-sort-by-label"
            value={sortBy}
            label="Sort by"
            onChange={e => setSortBy(e.target.value)}
            renderValue={(value) => (
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                {value === 'addedDate' ? (
                  <AccessTimeIcon fontSize="small" />
                ) : (
                  <SortByAlphaIcon fontSize="small" />
                )}
                <Typography variant="body2">{getSortLabel(value)}</Typography>
              </Box>
            )}
          >
            <MenuItem value="alpha">
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                <SortByAlphaIcon fontSize="small" />
                <Typography variant="body2">Title (A–Z)</Typography>
              </Box>
            </MenuItem>
            <MenuItem value="addedDate">
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                <AccessTimeIcon fontSize="small" />
                <Typography variant="body2">Recently Added</Typography>
              </Box>
            </MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="fullWidth"
        textColor="primary"
        indicatorColor="primary"
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab value="movie" label={`Movies (${movieCount})`} />
        <Tab value="tv" label={`TV Shows (${tvCount})`} />
      </Tabs>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {filtered.length === 0 && !error ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography color="text.secondary">
            {items.length === 0 ? 'No content in the catalogue yet.' : 'No results match your search.'}
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, 160px)',
            gap: 2.5,
            justifyContent: 'center',
          }}
        >
          {sortedFiltered.map(item => (
            <MediaCard key={item.id} item={item} onClick={() => onItemClick && onItemClick(item)} />
          ))}
        </Box>
      )}
    </Container>
  );
}

export default Catalogue;
